import { motion } from "framer-motion";
import { Camera } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import usePhotos from "../hooks/usePhotos";

const INGRESSO_IMG =
  "https://customer-assets-0z36b82j.emergentagent.net/job_casale-tradizione/artifacts/7a15a5d5dd7be451_2aa53f9e-6b37-4653-8c90-c438f46c1436.jpeg";
const CORTE_IMG =
  "https://customer-assets-0z36b82j.emergentagent.net/job_casale-tradizione/artifacts/6ac007e642e842e4_9dfca840-4b1c-4bc8-a0ed-9f8c57cc7734.jpeg";

export default function Storia() {
  const { lang } = useLang();
  const photos = usePhotos();
  return (
    <section
      id="storia"
      data-testid="storia-section-container"
      className="py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative order-2 lg:order-1"
          >
            <div className="gold-frame overflow-hidden">
              <img
                src={photos["storia-ingresso"] || INGRESSO_IMG}
                alt="L'arco d'ingresso del Ristorante Milano con la lanterna e la corte illuminata"
                className="w-full h-[480px] object-cover hover:scale-105 transition-transform duration-[1.5s]"
              />
            </div>
            <div className="absolute -bottom-6 -right-4 lg:-right-8 bg-[#6E1D24] text-[#FAF7F2] px-7 py-5 shadow-2xl">
              <p className="font-serif-display italic text-3xl leading-none">
                {lang === "it" ? "~35 anni" : "~35 years"}
              </p>
              <p className="text-[10px] uppercase tracking-[0.25em] mt-2 font-cinzel opacity-80">
                {lang === "it" ? "di attesa, poi il ritorno" : "of waiting, then the return"}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="order-1 lg:order-2"
          >
            <span className="eyebrow">{lang === "it" ? "La Storia" : "Our Story"}</span>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#FAF7F2] leading-[1.12]">
              {lang === "it" ? (
                <>Un nome che Casale
                <span className="italic text-[#C9A265]"> non ha mai dimenticato</span></>
              ) : (
                <>A name Casale
                <span className="italic text-[#C9A265]"> has never forgotten</span></>
              )}
            </h2>
            <p className="mt-8 text-base text-[#B0A89C] leading-relaxed">
              {lang === "it"
                ? "Più di trentacinque anni fa, in Piazza Castello, il Ristorante Milano era un punto di riferimento per chi amava la vera cucina piemontese: le domeniche in famiglia, i profumi che uscivano dalla cucina fin sulla piazza."
                : "More than thirty-five years ago, in Piazza Castello, Ristorante Milano was a landmark for lovers of true Piedmontese cuisine: family Sundays, aromas drifting from the kitchen onto the square."}
            </p>
            <p className="mt-6 text-base text-[#B0A89C] leading-relaxed">
              {lang === "it"
                ? "Oggi quelle porte si riaprono. Stesso indirizzo, stessa anima — ma uno sguardo nuovo: accanto ai grandi classici del territorio trovano spazio proposte alternative."
                : "Today those doors reopen. Same address, same soul — but with a fresh outlook: alongside the great classics of the region you will find creative dishes."}
            </p>
            <div className="mt-10 grid grid-cols-2 gap-6 border-t border-[rgba(201,162,101,0.18)] pt-8">
              {[
                { n: "41", l: { it: "Piazza Castello", en: "Piazza Castello" } },
                { n: "2", l: { it: "Anime in cucina", en: "Souls in the kitchen" } },
              ].map((s) => (
                <div key={s.l.it}>
                  <p className="font-serif-display text-3xl sm:text-4xl text-[#C9A265]">
                    {s.n}
                  </p>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[#7A7267] font-cinzel">
                    {s.l[lang]}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-24 lg:mt-32"
        >
          <div className="text-center">
            <span className="eyebrow">{lang === "it" ? "La Storia per Immagini" : "Our Story in Pictures"}</span>
            <h3 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl text-[#FAF7F2]">
              {lang === "it" ? (
                <>Ieri e oggi, <span className="italic text-[#C9A265]">sotto lo stesso arco</span></>
              ) : (
                <>Yesterday and today, <span className="italic text-[#C9A265]">under the same arch</span></>
              )}
            </h3>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 gap-6 lg:gap-10 max-w-4xl mx-auto">
            <figure
              data-testid="storia-foto-ieri"
              className={`group relative overflow-hidden rounded-2xl h-80 ${
                photos["storia-ieri"]
                  ? "border border-[rgba(255,255,255,0.08)] hover:border-[rgba(201,162,101,0.5)] transition-colors duration-500"
                  : "border border-dashed border-[rgba(201,162,101,0.35)] bg-[#171512] flex flex-col items-center justify-center text-center px-8"
              }`}
            >
              {photos["storia-ieri"] ? (
                <>
                  <img
                    src={photos["storia-ieri"]}
                    alt="Il Ristorante Milano in una foto d'archivio di tanti anni fa"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1.4s]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D0C0A]/70 via-transparent to-transparent" />
                </>
              ) : (
                <>
                  <Camera className="text-[#C9A265]" size={28} />
                  <p className="mt-4 font-serif-display italic text-xl text-[#B0A89C]">
                    {lang === "it" ? "La foto d'archivio del vecchio Milano" : "The archive photo of the old Milano"}
                  </p>
                  <p className="mt-2 text-xs text-[#7A7267] leading-relaxed">
                    {lang === "it"
                      ? "Stiamo raccogliendo gli scatti d'epoca: appena pronti, troveranno casa qui, accanto al presente."
                      : "We are collecting vintage shots: once ready, they will find their home here, beside the present."}
                  </p>
                </>
              )}
              <figcaption className="absolute top-4 left-5 text-[10px] uppercase tracking-[0.25em] text-[#C9A265] font-cinzel bg-[#0D0C0A]/60 backdrop-blur-sm px-3 py-1.5 rounded-full">
                {lang === "it" ? "Ieri" : "Yesterday"}
              </figcaption>
            </figure>
            <figure
              data-testid="storia-foto-oggi"
              className="group relative overflow-hidden rounded-2xl border border-[rgba(255,255,255,0.08)] hover:border-[rgba(201,162,101,0.5)] transition-colors duration-500 h-80"
            >
              <img
                src={photos["storia-oggi"] || CORTE_IMG}
                alt="La corte del Ristorante Milano oggi, con gli ombrelli colorati illuminati"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1.4s]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D0C0A]/70 via-transparent to-transparent" />
              <figcaption className="absolute top-4 left-5 text-[10px] uppercase tracking-[0.25em] text-[#C9A265] font-cinzel bg-[#0D0C0A]/60 backdrop-blur-sm px-3 py-1.5 rounded-full">
                {lang === "it" ? "Oggi" : "Today"}
              </figcaption>
            </figure>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
